import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const MODES = [
  ["off", "Off: no daily reminders", "Staff rely on the published rota."],
  ["all", "Every shift", "A reminder the evening before each shift."],
  ["overnight", "Overnight & sleepover shifts only", "Only shifts that run past midnight or are sleepovers."],
  ["changes", "Swaps & extra shifts only", "Only shifts picked up through a swap or an open-shift claim, reassigned, or added after the rota was published."],
];

export const ShiftReminderSettings = () => {
  const [mode, setMode] = useState(null);
  useEffect(() => {
    api.get("/biz/reminder-settings").then((r) => setMode(r.data.mode));
  }, []);
  if (!mode) return null;

  const save = async () => {
    try {
      await api.put("/biz/reminder-settings", { mode });
      toast.success("Reminder setting saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const preview = async () => {
    try {
      const { data } = await api.post("/biz/shift-reminder/preview");
      toast.success(data.demo ? `Demo business: preview skipped (would show ${data.staff_name}'s reminder)` : `Preview of ${data.staff_name}'s reminder sent to ${data.to}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const current = MODES.find((m) => m[0] === mode);

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="reminder-settings">
      <div>
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><BellRing className="h-5 w-5 text-indigo-600" />Shift reminders</h2>
        <p className="text-sm text-slate-500">Emailed to staff at 6pm the evening before their shift.</p>
      </div>
      <div className="space-y-1.5">
        <Label>Who gets a reminder?</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="reminder-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>{MODES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`reminder-mode-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-xs text-slate-500" data-testid="reminder-mode-help">{current?.[2]}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button className="rounded-full" onClick={save} data-testid="reminder-save-btn">Save</Button>
        <Button variant="outline" className="rounded-full" onClick={preview} data-testid="reminder-preview-btn">Send me a sample reminder</Button>
      </div>
    </div>
  );
};
